import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
    FaChartBar,
    FaCheck,
    FaChevronRight,
    FaDatabase,
    FaFileInvoiceDollar,
    FaFilePdf,
    FaLayerGroup,
    FaSearch,
    FaSignature,
    FaTrashAlt,
    FaUsers,
} from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { ViewTitle } from '../../reusable'
import styles from './styles.module.scss'

type Row = Record<string, unknown>
type Counts = Record<string, number>

interface Pagination {
    page: number
    pageSize: number
    total: number
    totalPages: number
}

const LENDER_CHART_LIMIT = 10

const formatLabel = (value: string) =>
    value
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())

// Enum-style column values arrive as HIRE_PURCHASE or 'closed', so they are normalised before display.
const humanise = (value: unknown) => formatLabel(String(value).toLowerCase())

const number = (value: unknown) => Number(value || 0).toLocaleString('en-GB')

const toDate = (value: unknown) => {
    if (typeof value !== 'string' && !(value instanceof Date)) return null
    const date = value instanceof Date ? value : new Date(value.replace(' ', 'T'))
    return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (value: unknown) => {
    const date = toDate(value)
    return date ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}

const formatDateTime = (value: unknown) => {
    const date = toDate(value)
    if (!date) return '—'
    return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString(
        'en-GB',
        { hour: '2-digit', minute: '2-digit' }
    )}`
}

// Valuation columns are varchar money values, so they are coerced before display.
const money = (value: unknown) => {
    const amount = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
    return Number.isFinite(amount) && String(value ?? '') !== ''
        ? amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 })
        : '—'
}

const text = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '—'
    return String(value)
}

// tinyint flags arrive as 0/1 and the two varchar flags as 'YES'/null.
const isSet = (value: unknown) =>
    value === 1 || value === true || (typeof value === 'string' && !['', '0', 'NO'].includes(value.trim().toUpperCase()))

const yesNo = (value: unknown) => (isSet(value) ? 'Yes' : 'No')

const errorMessage = (error: any) =>
    error?.response?.data?.message || error?.message || 'Something went wrong while loading SRDS data.'

const Status = ({ loading, error, empty }: { loading: boolean; error: string; empty?: string }) => {
    if (loading) return <div className={styles.status} role="status">Loading SRDS data…</div>
    if (error) return <div className={styles.error} role="alert">{error}</div>
    if (empty) return <div className={styles.status}>{empty}</div>
    return null
}

type Detail = { label: string; value: React.ReactNode; wide?: boolean }

const DetailList = ({ items }: { items: Detail[] }) => (
    <dl className={styles.detailList}>
        {items.map(({ label, value, wide }) => (
            <div className={wide ? `${styles.detailItem} ${styles.detailItemWide}` : styles.detailItem} key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
            </div>
        ))}
    </dl>
)

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className={styles.panel}>
        <h2>{title}</h2>
        {children}
    </section>
)

const Pill = ({ label, tone = 'neutral' }: { label: string; tone?: 'positive' | 'neutral' | 'warning' }) => (
    <span className={`${styles.pill} ${styles[`pill--${tone}`]}`}>{label}</span>
)

const SummaryStrip = ({ label, items }: { label: string; items: { label: string; value: string }[] }) => (
    <dl className={styles.summaryStrip} role="group" aria-label={label}>
        {items.map(({ label, value }) => (
            <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
            </div>
        ))}
    </dl>
)

const ProgressList = ({ rows, total }: { rows: Record<string, unknown>; total: number }) => {
    const entries = Object.entries(rows || {})
    if (!entries.length) return <div className={styles.status}>No data available.</div>
    return (
        <dl className={styles.progressList}>
            {entries.map(([key, value]) => {
                const count = Number(value || 0)
                const share = total > 0 ? Math.round((count / total) * 100) : 0
                return (
                    <div className={styles.progressRow} key={key}>
                        <dt>{formatLabel(key)}</dt>
                        <dd>
                            <span className={styles.progressTrack}>
                                <span style={{ width: `${share}%` }} />
                            </span>
                            <span className={styles.progressValue}>
                                {number(count)} <em>{share}%</em>
                            </span>
                        </dd>
                    </div>
                )
            })}
        </dl>
    )
}

type TransparencyRun = {
    id: string
    status: string
    dryRun?: boolean
    total?: number
    processed?: number
    updated?: number
    skipped?: number
    errors?: number
    currentClient?: string | null
    heartbeatAgeSeconds?: number | null
    errorMessage?: string | null
    samples?: {
        clientId?: number
        clientRef?: string | null
        source?: string
        before?: string
        after?: string
    }[]
    failures?: { clientId?: number; clientRef?: string | null; message?: string }[]
}

type TransparencyPreview = {
    totalEligible?: number
    totalAvailable?: number
    totalPending?: number
    totalRepaired?: number
}

const SignatureTransparencyDialog = ({ onClose }: { onClose: () => void }) => {
    const [preview, setPreview] = useState<TransparencyPreview | null>(null)
    const [confirmation, setConfirmation] = useState('')
    const [error, setError] = useState('')
    const [busy, setBusy] = useState<'preview' | 'start' | 'poll' | null>('preview')
    const [run, setRun] = useState<TransparencyRun | null>(null)
    const [adopted, setAdopted] = useState(false)

    useEffect(() => {
        api('/srds-signature-transparency-preview')
            .then(({ data }) => setPreview(data.preview))
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setBusy(null))
    }, [])

    useEffect(() => {
        if (!run?.id || run.status === 'complete' || run.status === 'failed') return
        let stopped = false
        let timer: ReturnType<typeof setTimeout> | null = null

        const poll = async () => {
            try {
                const { data } = await api('/srds-signature-transparency-status', { data: { runId: run.id } })
                if (stopped) return
                if (!data?.run) throw new Error(data?.message || 'Could not read backfill progress.')
                setRun(data.run)
                setError('')
                if (data.run.status === 'complete' || data.run.status === 'failed') {
                    setBusy(null)
                    return
                }
                setBusy('poll')
                timer = setTimeout(poll, 1000)
            } catch (err) {
                if (stopped) return
                setError(errorMessage(err))
                timer = setTimeout(poll, 3000)
            }
        }

        setBusy('poll')
        void poll()
        return () => {
            stopped = true
            if (timer) clearTimeout(timer)
        }
    }, [run?.id, run?.status])

    const start = async (dryRun: boolean) => {
        setBusy('start')
        setError('')
        try {
            const { data } = await api('/srds-signature-transparency-start', {
                data: dryRun
                    ? { dryRun: true }
                    : { dryRun: false, confirm: 'OVERWRITE_SIGNATURES' },
            })
            if (!data?.run?.id) throw new Error(data?.message || 'Could not start the signature backfill.')
            setAdopted(Boolean(data.existing))
            setRun(data.run)
        } catch (err) {
            setError(errorMessage(err))
            setBusy(null)
        }
    }

    const running = Boolean(run && !['complete', 'failed'].includes(run.status))
    const finished = run?.status === 'complete' || run?.status === 'failed'
    const stalled = running && (run?.heartbeatAgeSeconds ?? 0) >= 60
    const liveReady = confirmation.trim().toLowerCase() === 'overwrite'

    return (
        <div
            className={styles.modalBackdrop}
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="srds-signature-title">
                <h2 id="srds-signature-title">Make signatures transparent</h2>
                <p>
                    Removes the white canvas from each client&apos;s signature, then writes the same PNG to every
                    claim path and the stored signature record. Each repaired signature is stamped, so only clients
                    not yet recorded as transparent are processed; a signature that is already transparent is
                    stamped and left as it is.
                </p>
                {preview ? (
                    <ul className={styles.previewCounts}>
                        <li>
                            <span>Signed clients</span>
                            <strong>{number(preview.totalAvailable ?? preview.totalEligible)}</strong>
                        </li>
                        <li>
                            <span>Recorded as transparent</span>
                            <strong>{number(preview.totalRepaired ?? 0)}</strong>
                        </li>
                        <li>
                            <span>To process</span>
                            <strong>{number(preview.totalPending ?? preview.totalEligible)}</strong>
                        </li>
                    </ul>
                ) : (
                    <Status loading={!error} error={error} />
                )}
                <p className={styles.untouched}>
                    Dry run reports the work without writing files. A live run overwrites S3 signature PNGs and the
                    base64 stored on the client signature record, and stamps each record it repairs or finds already
                    transparent.
                </p>
                {run ? (
                    <ul className={styles.previewCounts} aria-live="polite">
                        <li>
                            <span>Status</span>
                            <strong>{run.dryRun ? `Dry run · ${run.status}` : run.status}</strong>
                        </li>
                        <li>
                            <span>Processed</span>
                            <strong>
                                {number(run.processed)} of {number(run.total)}
                            </strong>
                        </li>
                        <li>
                            <span>{run.dryRun ? 'Would update' : 'Updated'}</span>
                            <strong>{number(run.updated)}</strong>
                        </li>
                        <li>
                            <span>Already transparent</span>
                            <strong>{number(run.skipped)}</strong>
                        </li>
                        <li>
                            <span>Failed</span>
                            <strong>{number(run.errors)}</strong>
                        </li>
                    </ul>
                ) : null}
                {adopted && running ? (
                    <p className={styles.untouched}>
                        A signature backfill was already in progress, so this shows that run instead of starting a
                        new one.
                    </p>
                ) : null}
                {stalled ? (
                    <div className={styles.error} role="alert">
                        This run has not reported progress for {number(run?.heartbeatAgeSeconds)} seconds. It will be
                        released automatically, after which you can start a new run.
                    </div>
                ) : null}
                {run?.currentClient && running ? (
                    <p className={styles.untouched}>Currently processing {run.currentClient}.</p>
                ) : null}
                {run?.errorMessage ? (
                    <div className={styles.error} role="alert">
                        {run.errorMessage}
                    </div>
                ) : null}
                {run?.samples?.length ? (
                    <section className={styles.samples}>
                        <h3>
                            First {run.samples.length} signatures, before and after
                        </h3>
                        <p className={styles.untouched}>
                            The checkerboard shows through wherever the canvas is now transparent. Strokes should
                            look identical in both columns.
                        </p>
                        <ul>
                            {run.samples.map((sample) => {
                                const who = sample.clientRef || `Client ${sample.clientId}`
                                return (
                                    <li key={sample.clientId}>
                                        <span className={styles.sampleRef}>
                                            {who}
                                            {sample.source === 's3' ? ' · recovered from S3' : ''}
                                        </span>
                                        <div className={styles.samplePair}>
                                            <figure>
                                                <img
                                                    src={`data:image/png;base64,${sample.before}`}
                                                    alt={`Original signature for ${who}`}
                                                />
                                                <figcaption>Original</figcaption>
                                            </figure>
                                            <figure className={styles.sampleTransparent}>
                                                <img
                                                    src={`data:image/png;base64,${sample.after}`}
                                                    alt={`Transparent signature for ${who}`}
                                                />
                                                <figcaption>Transparent</figcaption>
                                            </figure>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </section>
                ) : null}
                {run?.failures?.length ? (
                    <ul className={styles.previewCounts}>
                        {run.failures.slice(0, 8).map((failure) => (
                            <li key={`${failure.clientId}-${failure.message}`}>
                                <span>{failure.clientRef || `Client ${failure.clientId}`}</span>
                                <strong>{failure.message}</strong>
                            </li>
                        ))}
                    </ul>
                ) : null}
                {!run || finished ? (
                    <>
                        <label className={styles.confirmLabel} htmlFor="srds-signature-confirm">
                            Type <strong>overwrite</strong> to confirm
                        </label>
                        <input
                            id="srds-signature-confirm"
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value)}
                            autoComplete="off"
                            disabled={running}
                        />
                    </>
                ) : null}
                {error && preview ? (
                    <div className={styles.error} role="alert">
                        {error}
                    </div>
                ) : null}
                <div className={styles.modalActions}>
                    <button type="button" className={styles.secondaryButton} onClick={onClose}>
                        {running ? 'Hide' : finished ? 'Close' : 'Cancel'}
                    </button>
                    {!finished ? (
                        <>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                disabled={Boolean(busy) || !preview}
                                onClick={() => start(true)}
                            >
                                {busy && run?.dryRun !== false ? 'Starting…' : 'Dry run'}
                            </button>
                            <button
                                type="button"
                                className={styles.dangerButton}
                                disabled={!liveReady || Boolean(busy) || !preview}
                                onClick={() => start(false)}
                            >
                                {busy && run?.dryRun === false ? 'Starting…' : 'Overwrite signatures'}
                            </button>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

type RepairFailure = {
    clientId?: number
    clientRef?: string | null
    email?: string | null
    category?: string
    message?: string
}

type RepairRun = {
    id: string
    status: string
    scope?: 'all' | 'spreadsheet' | 'client'
    total?: number
    processed?: number
    fixed?: number
    failed?: number
    failedByCategory?: Record<string, number>
    currentClient?: string | null
    unmatchedEmails?: string[]
    failures?: RepairFailure[]
    errorMessage?: string | null
}

type RepairClientOption = {
    id: number
    title?: string | null
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    client_ref?: string | null
}

const repairClientName = (client: RepairClientOption) =>
    [client.title, client.firstname, client.lastname].filter(Boolean).join(' ') || 'Unnamed'

const repairScopeLabel = (scope?: RepairRun['scope']) => {
    if (scope === 'spreadsheet') return 'Spreadsheet clients'
    if (scope === 'client') return 'Selected client'
    return 'All clients'
}

const FAILURE_LABELS: Record<string, string> = {
    network: 'Network / endpoint',
    rejected: 'Rejected by repair service',
    decode: 'Base64 or JSON decoding',
    missing: 'No stored JSON',
    upload: 'S3 write',
}

const readFileAsBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const dataUrl = String(reader.result || '')
            resolve(dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl)
        }
        reader.onerror = () => reject(reader.error || new Error('Could not read the file'))
        reader.readAsDataURL(file)
    })

const csvCell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const downloadFailuresCsv = (run: RepairRun) => {
    const rows = [
        ['email', 'client_ref', 'client_id', 'category', 'message'],
        ...(run.failures || []).map((failure) => [
            failure.email,
            failure.clientRef,
            failure.clientId,
            failure.category,
            failure.message,
        ]),
    ]
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n')
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `srds-credit-report-repair-failures-${run.id.slice(0, 8)}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
}

const CreditReportRepairDialog = ({ onClose }: { onClose: () => void }) => {
    const [scope, setScope] = useState<'all' | 'spreadsheet' | 'client'>('all')
    const [file, setFile] = useState<{ name: string; data: string } | null>(null)
    const [clientQuery, setClientQuery] = useState('')
    const [clientResults, setClientResults] = useState<RepairClientOption[]>([])
    const [clientSearchLoading, setClientSearchLoading] = useState(false)
    const [selectedClient, setSelectedClient] = useState<RepairClientOption | null>(null)
    const [confirmation, setConfirmation] = useState('')
    const [error, setError] = useState('')
    const [busy, setBusy] = useState<'start' | 'poll' | null>(null)
    const [run, setRun] = useState<RepairRun | null>(null)
    const [adopted, setAdopted] = useState(false)

    useEffect(() => {
        if (!run?.id || run.status !== 'running') return
        let stopped = false
        let timer: ReturnType<typeof setTimeout> | null = null

        const poll = async () => {
            try {
                const { data } = await api('/srds-credit-report-repair-status', { data: { runId: run.id } })
                if (stopped) return
                if (!data?.run) throw new Error(data?.message || 'Could not read repair progress.')
                setRun(data.run)
                setError('')
                if (data.run.status !== 'running') {
                    setBusy(null)
                    return
                }
                setBusy('poll')
                timer = setTimeout(poll, 1000)
            } catch (err) {
                if (stopped) return
                setError(errorMessage(err))
                timer = setTimeout(poll, 3000)
            }
        }

        setBusy('poll')
        void poll()
        return () => {
            stopped = true
            if (timer) clearTimeout(timer)
        }
    }, [run?.id, run?.status])

    useEffect(() => {
        if (scope !== 'client') return
        const trimmed = clientQuery.trim()
        if (trimmed.length < 2) {
            setClientResults([])
            setClientSearchLoading(false)
            return
        }
        let cancelled = false
        const timer = window.setTimeout(() => {
            setClientSearchLoading(true)
            api('/srds-clients', { data: { page: 1, pageSize: 10, search: trimmed } })
                .then(({ data }) => {
                    if (cancelled) return
                    setClientResults(Array.isArray(data?.clients) ? data.clients : [])
                })
                .catch((err) => {
                    if (cancelled) return
                    setClientResults([])
                    setError(errorMessage(err))
                })
                .finally(() => {
                    if (!cancelled) setClientSearchLoading(false)
                })
        }, 300)
        return () => {
            cancelled = true
            window.clearTimeout(timer)
        }
    }, [clientQuery, scope])

    const chooseFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const chosen = event.target.files?.[0]
        event.target.value = ''
        if (!chosen) return
        try {
            setFile({ name: chosen.name, data: await readFileAsBase64(chosen) })
            setError('')
        } catch (err) {
            setError(errorMessage(err))
        }
    }

    const changeScope = (next: 'all' | 'spreadsheet' | 'client') => {
        setScope(next)
        setError('')
    }

    const start = async () => {
        setBusy('start')
        setError('')
        try {
            const { data } = await api('/srds-credit-report-repair-start', {
                data: {
                    confirm: 'REPAIR_CREDIT_REPORTS',
                    ...(scope === 'spreadsheet' && file ? { fileData: file.data } : {}),
                    ...(scope === 'client' && selectedClient ? { clientIds: [selectedClient.id] } : {}),
                },
            })
            if (!data?.run?.id) throw new Error(data?.message || 'Could not start the credit report repair.')
            setAdopted(false)
            setRun(data.run)
            if (data.run.status !== 'running') setBusy(null)
        } catch (err: any) {
            const activeRun = err?.response?.data?.run
            if (err?.response?.status === 409 && activeRun?.id) {
                setAdopted(true)
                setRun(activeRun)
                return
            }
            setError(errorMessage(err))
            setBusy(null)
        }
    }

    const running = run?.status === 'running'
    const finished = Boolean(run) && !running
    const ready =
        confirmation.trim().toLowerCase() === 'repair' &&
        (scope === 'all' || (scope === 'spreadsheet' && Boolean(file)) || (scope === 'client' && Boolean(selectedClient))) &&
        !busy
    const categories = Object.entries(run?.failedByCategory || {}).filter(([, count]) => Number(count) > 0)

    return (
        <div
            className={styles.modalBackdrop}
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div
                className={`${styles.modal} ${styles.modalWide}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="srds-repair-title"
            >
                <h2 id="srds-repair-title">Repair credit reports</h2>
                <p>
                    Sends each client&apos;s stored credit report JSON to the repair service one client at a time,
                    then overwrites the credit report PDF and JSON in S3 with the repaired versions.
                </p>
                {!run ? (
                    <>
                        <fieldset className={styles.scopeOptions} style={{ border: 0, padding: 0 }}>
                            <legend className={styles.srOnly}>Which clients to repair</legend>
                            <label
                                className={
                                    scope === 'all' ? `${styles.scopeOption} ${styles.scopeOptionSelected}` : styles.scopeOption
                                }
                            >
                                <input
                                    type="radio"
                                    name="srds-repair-scope"
                                    value="all"
                                    checked={scope === 'all'}
                                    onChange={() => changeScope('all')}
                                />
                                <div>
                                    <span>All SRDS clients</span>
                                    <small>Every client in the SRDS database, in ID order.</small>
                                </div>
                            </label>
                            <label
                                className={
                                    scope === 'client'
                                        ? `${styles.scopeOption} ${styles.scopeOptionSelected}`
                                        : styles.scopeOption
                                }
                            >
                                <input
                                    type="radio"
                                    name="srds-repair-scope"
                                    value="client"
                                    checked={scope === 'client'}
                                    onChange={() => changeScope('client')}
                                />
                                <div>
                                    <span>One client</span>
                                    <small>Search by name or email, then repair only that client&apos;s credit report.</small>
                                </div>
                            </label>
                            <label
                                className={
                                    scope === 'spreadsheet'
                                        ? `${styles.scopeOption} ${styles.scopeOptionSelected}`
                                        : styles.scopeOption
                                }
                            >
                                <input
                                    type="radio"
                                    name="srds-repair-scope"
                                    value="spreadsheet"
                                    checked={scope === 'spreadsheet'}
                                    onChange={() => changeScope('spreadsheet')}
                                />
                                <div>
                                    <span>Clients from a spreadsheet</span>
                                    <small>
                                        Upload a CSV or XLSX with an email column. Only matching SRDS clients are
                                        repaired.
                                    </small>
                                </div>
                            </label>
                        </fieldset>
                        {scope === 'client' ? (
                            <div className={styles.clientPicker}>
                                {selectedClient ? (
                                    <div className={styles.clientPickerSelected}>
                                        <div>
                                            <strong>{repairClientName(selectedClient)}</strong>
                                            <span>
                                                {[selectedClient.email, selectedClient.client_ref].filter(Boolean).join(' · ') ||
                                                    'No email address'}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.secondaryButton}
                                            onClick={() => {
                                                setSelectedClient(null)
                                                setClientQuery('')
                                                setClientResults([])
                                            }}
                                        >
                                            Change
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <label htmlFor="srds-repair-client-search">Search clients</label>
                                        <div className={styles.clientPickerSearch}>
                                            <FaSearch aria-hidden="true" />
                                            <input
                                                id="srds-repair-client-search"
                                                value={clientQuery}
                                                onChange={(event) => {
                                                    setClientQuery(event.target.value)
                                                    setError('')
                                                }}
                                                placeholder="Name or email address…"
                                                autoComplete="off"
                                            />
                                        </div>
                                        {clientSearchLoading ? (
                                            <p className={styles.untouched}>Searching…</p>
                                        ) : null}
                                        {!clientSearchLoading && clientQuery.trim().length >= 2 ? (
                                            clientResults.length ? (
                                                <ul className={styles.clientPickerResults} role="listbox" aria-label="Matching clients">
                                                    {clientResults.map((client) => (
                                                        <li key={client.id}>
                                                            <button
                                                                type="button"
                                                                role="option"
                                                                aria-selected={false}
                                                                onClick={() => {
                                                                    setSelectedClient(client)
                                                                    setClientQuery('')
                                                                    setClientResults([])
                                                                    setError('')
                                                                }}
                                                            >
                                                                <strong>{repairClientName(client)}</strong>
                                                                <span>
                                                                    {[client.email, client.client_ref].filter(Boolean).join(' · ') ||
                                                                        'No email address'}
                                                                </span>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className={styles.untouched}>No clients match that search.</p>
                                            )
                                        ) : null}
                                        {!clientSearchLoading && clientQuery.trim().length > 0 && clientQuery.trim().length < 2 ? (
                                            <p className={styles.untouched}>Type at least 2 characters to search.</p>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        ) : null}
                        {scope === 'spreadsheet' ? (
                            <div className={styles.fileField}>
                                <label htmlFor="srds-repair-file">Spreadsheet file</label>
                                <input
                                    id="srds-repair-file"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={chooseFile}
                                />
                                {file ? <strong>{file.name}</strong> : <em>No file chosen</em>}
                            </div>
                        ) : null}
                        <p className={styles.untouched}>
                            The repair service removes previous industry-search data and returns a fresh PDF and JSON,
                            which overwrite the existing credit report files. Clients that fail because of a network,
                            service or decoding problem are listed at the end so they can be sent again.
                        </p>
                    </>
                ) : null}
                {run ? (
                    <ul className={styles.previewCounts} aria-live="polite">
                        <li>
                            <span>Status</span>
                            <strong>{run.status}</strong>
                        </li>
                        <li>
                            <span>Scope</span>
                            <strong>{repairScopeLabel(run.scope)}</strong>
                        </li>
                        <li>
                            <span>Processed</span>
                            <strong>
                                {number(run.processed)} of {number(run.total)}
                            </strong>
                        </li>
                        <li>
                            <span>Fixed</span>
                            <strong>{number(run.fixed)}</strong>
                        </li>
                        <li>
                            <span>Failed</span>
                            <strong>{number(run.failed)}</strong>
                        </li>
                    </ul>
                ) : null}
                {adopted && running ? (
                    <p className={styles.untouched}>
                        A credit report repair was already in progress, so this shows that run instead of starting a
                        new one.
                    </p>
                ) : null}
                {run?.currentClient && running ? (
                    <p className={styles.untouched}>Currently processing {run.currentClient}.</p>
                ) : null}
                {run?.errorMessage ? (
                    <div className={styles.error} role="alert">
                        {run.errorMessage}
                    </div>
                ) : null}
                {finished && categories.length ? (
                    <section className={styles.samples}>
                        <h3>Failures by cause</h3>
                        <ul className={styles.previewCounts}>
                            {categories.map(([category, count]) => (
                                <li key={category}>
                                    <span>{FAILURE_LABELS[category] || formatLabel(category)}</span>
                                    <strong>{number(count)}</strong>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}
                {finished && run?.unmatchedEmails?.length ? (
                    <section className={styles.samples}>
                        <h3>
                            {number(run.unmatchedEmails.length)} spreadsheet email
                            {run.unmatchedEmails.length === 1 ? '' : 's'} did not match an SRDS client
                        </h3>
                        <p className={styles.untouched}>{run.unmatchedEmails.join(', ')}</p>
                    </section>
                ) : null}
                {finished && run?.failures?.length ? (
                    <section className={styles.samples}>
                        <h3>Failed clients</h3>
                        <p className={styles.untouched}>
                            Download the list and upload it as the spreadsheet on a new run to retry just these
                            clients.
                        </p>
                        <div className={styles.reportTableWrap}>
                            <table className={styles.reportTable}>
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Email</th>
                                        <th>Cause</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {run.failures.map((failure) => (
                                        <tr key={`${failure.clientId}-${failure.category}`}>
                                            <td>{failure.clientRef || `Client ${failure.clientId}`}</td>
                                            <td>{failure.email || '—'}</td>
                                            <td>{FAILURE_LABELS[failure.category || ''] || failure.category}</td>
                                            <td>{failure.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                ) : null}
                {finished && run && !run.failed && !run.errorMessage ? (
                    <p className={styles.untouched}>Every processed client was repaired successfully.</p>
                ) : null}
                {!run ? (
                    <>
                        <label className={styles.confirmLabel} htmlFor="srds-repair-confirm">
                            Type <strong>repair</strong> to confirm
                        </label>
                        <input
                            id="srds-repair-confirm"
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value)}
                            autoComplete="off"
                        />
                    </>
                ) : null}
                {error ? (
                    <div className={styles.error} role="alert">
                        {error}
                    </div>
                ) : null}
                <div className={styles.modalActions}>
                    <button type="button" className={styles.secondaryButton} onClick={onClose}>
                        {running ? 'Hide' : finished ? 'Close' : 'Cancel'}
                    </button>
                    {finished && run?.failures?.length ? (
                        <button type="button" className={styles.secondaryButton} onClick={() => downloadFailuresCsv(run)}>
                            Download failed clients CSV
                        </button>
                    ) : null}
                    {!run ? (
                        <button type="button" className={styles.dangerButton} disabled={!ready} onClick={start}>
                            {busy === 'start' ? 'Starting…' : 'Repair credit reports'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

const ResetDialog = ({ onClose, onReset }: { onClose: () => void; onReset: () => void }) => {
    const [counts, setCounts] = useState<Counts | null>(null)
    const [confirmation, setConfirmation] = useState('')
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        api('/srds-reset-preview')
            .then(({ data }) => setCounts(data.counts))
            .catch((err) => setError(errorMessage(err)))
    }, [])

    const submit = async () => {
        setBusy(true)
        setError('')
        try {
            await api('/srds-reset', { data: { confirm: 'DELETE' } })
            onReset()
        } catch (err) {
            setError(errorMessage(err))
            setBusy(false)
        }
    }

    return (
        <div className={styles.modalBackdrop} role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose()
        }}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="srds-reset-title">
                <h2 id="srds-reset-title">Reset SRDS data</h2>
                <p>This permanently deletes records from the five SRDS reporting tables:</p>
                {counts ? (
                    <ul className={styles.previewCounts}>
                        {['clients', 'leads', 'lead_states', 'signature_links', 'errors'].map((table) => (
                            <li key={table}><span>{formatLabel(table)}</span><strong>{number(counts[table])}</strong></li>
                        ))}
                    </ul>
                ) : <Status loading={!error} error={error} />}
                <p className={styles.untouched}>
                    ViewThru batch records and files stored in S3 are not affected and will stay untouched.
                </p>
                <label className={styles.confirmLabel} htmlFor="srds-reset-confirm">
                    Type <strong>delete</strong> to confirm
                </label>
                <input
                    id="srds-reset-confirm"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    autoComplete="off"
                />
                {error && counts ? <div className={styles.error} role="alert">{error}</div> : null}
                <div className={styles.modalActions}>
                    <button type="button" className={styles.secondaryButton} onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className={styles.dangerButton}
                        disabled={confirmation.trim().toLowerCase() !== 'delete' || busy || !counts}
                        onClick={submit}
                    >
                        {busy ? 'Deleting…' : 'Delete SRDS data'}
                    </button>
                </div>
            </div>
        </div>
    )
}

const Overview = ({ refreshToken }: { refreshToken: number }) => {
    const [overview, setOverview] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        setLoading(true)
        setError('')
        api('/srds-overview')
            .then(({ data }) => setOverview(data.overview))
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setLoading(false))
    }, [refreshToken])

    if (loading || error || !overview) return <Status loading={loading} error={error} empty={!loading && !error ? 'No overview data.' : undefined} />

    const allLenders = overview.lenders || []
    const lenders = allLenders.slice(0, LENDER_CHART_LIMIT)
    const largestLenderClaimCount = Math.max(1, ...lenders.map((row: any) => Number(row.claims || 0)))
    const { total: leadStateTotal = 0, ...leadStateFlags } = overview.leadStates || {}
    const { total: signatureTotal = 0, ...signatureFlags } = overview.signatures || {}
    const clientCount = Number(overview.counts?.clients || 0)
    const claimCount = Number(overview.counts?.leads || 0)
    const averageClaimsPerClient = clientCount ? claimCount / clientCount : 0
    const headlineMetrics = [
        { label: 'Claims', value: number(claimCount), hint: `Across ${number(allLenders.length)} lenders`, icon: <FaLayerGroup /> },
        { label: 'Clients', value: number(clientCount), hint: 'Imported into SRDS', icon: <FaUsers /> },
        {
            label: 'Average claims per client',
            value: averageClaimsPerClient.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
            hint: 'Claims divided by clients',
            icon: <FaChartBar />,
        },
    ]

    return (
        <>
            <section className={styles.cardGrid} aria-label="SRDS headline metrics">
                {headlineMetrics.map((metric) => (
                    <article className={styles.statCard} key={metric.label}>
                        <span className={styles.statIcon} aria-hidden="true">{metric.icon}</span>
                        <span className={styles.statLabel}>{metric.label}</span>
                        <strong>{metric.value}</strong>
                        <span className={styles.statHint}>{metric.hint}</span>
                    </article>
                ))}
            </section>

            <div className={styles.twoColumns}>
                <section className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <div>
                            <h2>Claims by lender</h2>
                            <p>Top {LENDER_CHART_LIMIT} lenders by claim volume.</p>
                        </div>
                        <span className={styles.totalPill}>{number(overview.counts?.leads)} claims</span>
                    </div>
                    {!lenders.length ? (
                        <div className={styles.status}>No lender data is available.</div>
                    ) : (
                        <div className={styles.lenderChart} role="img" aria-label="Claims by lender bar chart">
                            {lenders.map((row: any) => (
                                <div className={styles.lenderBarRow} key={row.lender}>
                                    <div className={styles.lenderBarLabel}>
                                        <strong title={row.lender}>{row.lender}</strong>
                                        <span>{number(row.claims)}</span>
                                    </div>
                                    <div className={styles.lenderBarTrack}>
                                        <span style={{ width: `${Math.max(2, (Number(row.claims || 0) / largestLenderClaimCount) * 100)}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <div>
                            <h2>Claim progress</h2>
                            <p>Workflow and signature milestones across the schema.</p>
                        </div>
                    </div>
                    <ProgressList rows={leadStateFlags} total={leadStateTotal} />
                    <h3 className={styles.subheading}>Signature links</h3>
                    <ProgressList rows={signatureFlags} total={signatureTotal} />
                </section>
            </div>
        </>
    )
}

const ClientTable = ({ refreshToken }: { refreshToken: number }) => {
    const navigate = useNavigate()
    const [input, setInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [clients, setClients] = useState<any[]>([])
    const [pagination, setPagination] = useState<Pagination>({ page: 1, pageSize: 25, total: 0, totalPages: 0 })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextSearch = input.trim()
            setSearch((current) => {
                if (current !== nextSearch) setPage(1)
                return nextSearch
            })
        }, 350)
        return () => window.clearTimeout(timer)
    }, [input])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError('')
        api('/srds-clients', { data: { page, pageSize: 25, search } })
            .then(({ data }) => {
                if (cancelled) return
                setClients(data.clients || [])
                setPagination(data.pagination)
            })
            .catch((err) => {
                if (!cancelled) setError(errorMessage(err))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [page, search, refreshToken])

    return (
        <section className={styles.section} aria-label="SRDS clients">
            <div className={styles.sectionHeader}>
                <div><h2>Clients</h2><p>Search by client details, case reference, policy number or lender.</p></div>
                <label className={styles.searchLabel}>
                    <span className={styles.srOnly}>Search SRDS clients</span><FaSearch aria-hidden="true" />
                    <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Search clients…" />
                </label>
            </div>
            <Status loading={loading} error={error} empty={!loading && !clients.length ? 'No clients match this search.' : undefined} />
            {!loading && !error && clients.length ? (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.clientTable}>
                            <thead><tr><th>Client</th><th>Client reference</th><th>CFA signed</th><th>Claims</th><th>Imported</th><th><span className={styles.srOnly}>View</span></th></tr></thead>
                            <tbody>{clients.map((client) => (
                                <tr
                                    key={client.id}
                                    className={styles.clickableRow}
                                    onClick={() => navigate(`/srds/client/${client.id}`)}
                                >
                                    <td className={styles.clientIdentityCell}>
                                        <span className={styles.clientAvatar} aria-hidden="true">
                                            {`${client.firstname?.[0] || ''}${client.lastname?.[0] || ''}`.toUpperCase() || '?'}
                                        </span>
                                        <span className={styles.clientIdentity}>
                                            <Link to={`/srds/client/${client.id}`}>
                                                {[client.title, client.firstname, client.lastname].filter(Boolean).join(' ') || 'Unnamed'}
                                            </Link>
                                            <span>{client.email || 'No email address'}</span>
                                            {client.telephone ? <span>{client.telephone}</span> : null}
                                        </span>
                                    </td>
                                    <td><span className={styles.referenceChip}>{client.client_ref || '—'}</span></td>
                                    <td>{client.cfa_signed_date ? formatDate(client.cfa_signed_date) : <span className={styles.muted}>Not signed</span>}</td>
                                    <td><span className={styles.claimCount}>{number(client.claim_count)}</span></td>
                                    <td>{formatDate(client.created_at)}</td>
                                    <td className={styles.viewCell}><FaChevronRight aria-hidden="true" /></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                    <div className={styles.pagination}>
                        <span>Page {pagination.page} of {pagination.totalPages || 1} · {number(pagination.total)} clients</span>
                        <div>
                            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
                            <button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</button>
                        </div>
                    </div>
                </>
            ) : null}
        </section>
    )
}

type ActionItem = {
    label: string
    icon: React.ReactNode
    onSelect: () => void
    danger?: boolean
}

const ActionsMenu = ({ items }: { items: ActionItem[] }) => {
    const [open, setOpen] = useState(false)
    const wrapper = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: MouseEvent) => {
            if (!wrapper.current?.contains(event.target as Node)) setOpen(false)
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    return (
        <div className={styles.actionsMenu} ref={wrapper}>
            <button
                type="button"
                className={open ? `${styles.secondaryButton} ${styles.actionsMenuOpen}` : styles.secondaryButton}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                Actions <span className={styles.caret} aria-hidden="true" />
            </button>
            {open ? (
                <ul className={styles.actionsMenuList} role="menu" aria-label="SRDS actions">
                    {items.map((item, index) => (
                        <li key={item.label} role="none">
                            {item.danger && index > 0 ? <div className={styles.actionsMenuDivider} role="none" /> : null}
                            <button
                                type="button"
                                role="menuitem"
                                className={
                                    item.danger
                                        ? `${styles.actionsMenuItem} ${styles.actionsMenuItemDanger}`
                                        : styles.actionsMenuItem
                                }
                                onClick={() => {
                                    setOpen(false)
                                    item.onSelect()
                                }}
                            >
                                {item.icon}
                                {item.label}
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    )
}

export const SrdsHomeView = () => {
    const [isAdmin, setIsAdmin] = useState(false)
    const [showReset, setShowReset] = useState(false)
    const [showSignatures, setShowSignatures] = useState(false)
    const [showCreditRepair, setShowCreditRepair] = useState(false)
    const [refreshToken, setRefreshToken] = useState(0)

    useEffect(() => {
        api('/users-page-auth')
            .then(({ data }) => setIsAdmin(data?.isAdmin === true))
            .catch(() => setIsAdmin(false))
    }, [])

    const refreshed = () => {
        setShowReset(false)
        setRefreshToken((value) => value + 1)
    }

    return (
        <div className={styles.page}>
            <ViewTitle
                text="SRDS Dashboard"
                button={
                    isAdmin ? (
                        <div className={styles.titleActions}>
                            <ActionsMenu
                                items={[
                                    {
                                        label: 'Repair credit reports',
                                        icon: <FaFilePdf aria-hidden="true" />,
                                        onSelect: () => setShowCreditRepair(true),
                                    },
                                    {
                                        label: 'Make signatures transparent',
                                        icon: <FaSignature aria-hidden="true" />,
                                        onSelect: () => setShowSignatures(true),
                                    },
                                    {
                                        label: 'Reset SRDS data',
                                        icon: <FaTrashAlt aria-hidden="true" />,
                                        onSelect: () => setShowReset(true),
                                        danger: true,
                                    },
                                ]}
                            />
                        </div>
                    ) : undefined
                }
            />
            <div className={styles.intro}><FaDatabase aria-hidden="true" /><span>Sentinel Reporting Data Source overview and records.</span></div>
            <Overview refreshToken={refreshToken} />
            <ClientTable refreshToken={refreshToken} />
            {showCreditRepair ? <CreditReportRepairDialog onClose={() => setShowCreditRepair(false)} /> : null}
            {showSignatures ? <SignatureTransparencyDialog onClose={() => setShowSignatures(false)} /> : null}
            {showReset ? <ResetDialog onClose={() => setShowReset(false)} onReset={refreshed} /> : null}
        </div>
    )
}

const DetailShell = ({
    title,
    breadcrumb,
    children,
}: {
    title: string
    breadcrumb?: React.ReactNode
    children: React.ReactNode
}) => (
    <div className={styles.page}>
        <ViewTitle text={title} />
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            <Link to="/srds-home">SRDS dashboard</Link>{breadcrumb}
        </nav>
        {children}
    </div>
)

const initials = (client: Row) =>
    [client.firstname, client.lastname]
        .map((part) => String(part || '').trim().charAt(0).toUpperCase())
        .filter(Boolean)
        .join('') || '?'

const clientName = (client: Row) =>
    [client.title, client.firstname, client.lastname].filter(Boolean).join(' ') || `Client ${client.id}`

const addressLines = (client: Row) =>
    [
        [client.flat, client.house_name].filter(Boolean).join(', '),
        [client.house_number, client.street].filter(Boolean).join(' '),
        client.town_city,
        client.county,
        client.postcode,
    ]
        .map((line) => String(line || '').trim())
        .filter(Boolean)

const IdentityHeader = ({
    badge,
    title,
    reference,
    pills,
    aside,
}: {
    badge: React.ReactNode
    title: string
    reference?: unknown
    pills: React.ReactNode
    aside?: React.ReactNode
}) => (
    <header className={styles.identityHeader}>
        <span className={styles.identityBadge} aria-hidden="true">{badge}</span>
        <div className={styles.identityMain}>
            <h1>{title}</h1>
            {reference ? <p className={styles.identityRef}>{String(reference)}</p> : null}
            <div className={styles.pillRow}>{pills}</div>
        </div>
        {aside ? <div className={styles.identityAside}>{aside}</div> : null}
    </header>
)

const FileLink = ({ label, path }: { label: string; path: unknown }) => (
    <div className={styles.fileRow}>
        <span className={styles.fileLabel}>{label}</span>
        {path ? <code className={styles.filePath}>{String(path)}</code> : <span className={styles.muted}>Not available</span>}
    </div>
)

const ClaimSummaryCard = ({ lead, state, signature }: { lead: Row; state?: Row; signature?: Row }) => (
    <article className={styles.claimRow}>
        <div className={styles.claimRowHead}>
            <div>
                <h3>{text(lead.lender)}</h3>
                <p className={styles.claimRowRef}>{text(lead.case_ref)}</p>
            </div>
            <div className={styles.claimRowActions}>
                <span className={styles.claimRowValue}>{money(lead.quantum_value)}</span>
                <Link className={styles.secondaryButton} to={`/srds/claim/${lead.id}`}>
                    View claim <FaChevronRight aria-hidden="true" />
                </Link>
            </div>
        </div>
        <div className={styles.pillRow}>
            {isSet(lead.base_claim) ? <Pill label="Base claim" tone="neutral" /> : null}
            {lead.account_type ? <Pill label={humanise(lead.account_type)} tone="neutral" /> : null}
            {lead.agreement_status ? <Pill label={humanise(lead.agreement_status)} tone="neutral" /> : null}
            {isSet(state?.cfa_signed) ? <Pill label="CFA signed" tone="positive" /> : <Pill label="CFA outstanding" tone="warning" />}
            {isSet(state?.full_credit_check_complete) ? <Pill label="Credit check complete" tone="positive" /> : null}
            {isSet(state?.cancelled) ? <Pill label="Cancelled" tone="warning" /> : null}
        </div>
        <DetailList
            items={[
                { label: 'Policy number', value: text(lead.policy_number) },
                { label: 'Start date', value: formatDate(lead.start_date) },
                { label: 'Term', value: lead.length_of_contract ? `${text(lead.length_of_contract)} months` : '—' },
                { label: 'Monthly repayment', value: money(lead.monthly_repayment) },
                { label: 'Total payable', value: money(lead.total_amount_payable) },
                { label: 'Commission', value: money(lead.commission) },
                { label: 'Statutory interest', value: money(lead.stat_interest) },
                { label: 'CFA signed on', value: formatDate(state?.cfa_signed_date ?? signature?.signature_date) },
            ]}
        />
    </article>
)

const ClientBreakdown = ({
    client,
    leads,
    leadStates,
    signatures,
}: {
    client: Row
    leads: Row[]
    leadStates: Row[]
    signatures: Row[]
}) => {
    const stateByCase = new Map(leadStates.map((state) => [String(state.case_ref), state]))
    const signatureByCase = new Map(signatures.map((signature) => [String(signature.case_ref), signature]))
    const totalValue = leads.reduce((sum, lead) => sum + (Number(String(lead.quantum_value ?? '').replace(/[^0-9.-]/g, '')) || 0), 0)
    const lenders = new Set(leads.map((lead) => String(lead.lender || '')).filter(Boolean))
    const cfaSignedCount = leads.filter((lead) => isSet(stateByCase.get(String(lead.case_ref))?.cfa_signed)).length

    return (
        <>
            <IdentityHeader
                badge={initials(client)}
                title={clientName(client)}
                reference={client.client_ref}
                pills={<>
                    {isSet(client.aml) ? <Pill label="AML verified" tone="positive" /> : <Pill label="AML outstanding" tone="warning" />}
                    {isSet(client.presigned) ? <Pill label="Pre-signed" tone="neutral" /> : null}
                    {isSet(client.credit_check_rerun) ? <Pill label="Credit check rerun" tone="neutral" /> : null}
                    {isSet(client.il_selected) ? <Pill label="IL selected" tone="neutral" /> : null}
                </>}
                aside={<SummaryStrip
                    label="Client summary"
                    items={[
                        { label: 'Claims', value: number(leads.length) },
                        { label: 'Lenders', value: number(lenders.size) },
                        { label: 'CFAs signed', value: `${number(cfaSignedCount)} of ${number(leads.length)}` },
                        { label: 'Total claim value', value: money(totalValue) },
                    ]}
                />}
            />
            <div className={styles.panelGrid}>
                <Panel title="Contact">
                    <DetailList
                        items={[
                            { label: 'Email', value: text(client.email) },
                            { label: 'Telephone', value: text(client.telephone) },
                            { label: 'Date of birth', value: formatDate(client.dob) },
                            { label: 'Opted in', value: formatDateTime(client.opt_in_datetime) },
                        ]}
                    />
                </Panel>
                <Panel title="Address">
                    {addressLines(client).length ? (
                        <address className={styles.address}>
                            {addressLines(client).map((line) => <span key={line}>{line}</span>)}
                        </address>
                    ) : (
                        <p className={styles.muted}>No address recorded.</p>
                    )}
                </Panel>
                <Panel title="Credit &amp; consent">
                    <DetailList
                        items={[
                            { label: 'Experian reference', value: text(client.experian_reference) },
                            { label: 'PDF claims found', value: number(client.pdf_claims_found) },
                            { label: 'Credit report repaired', value: yesNo(client.cr_repair) },
                            { label: 'Voluntary arrangements', value: yesNo(client.voluntary_arrangements) },
                            { label: 'CCJs', value: yesNo(client.ccjs) },
                            { label: 'Open banking click', value: formatDateTime(client.open_banking_click_date) },
                            { label: 'Imported', value: formatDateTime(client.created_at) },
                        ]}
                    />
                </Panel>
                <Panel title="Documents">
                    <FileLink label="Credit report" path={client.credit_report_link} />
                    <FileLink label="ID / AML document" path={client.id_link} />
                </Panel>
            </div>
            <section className={styles.panel}>
                <h2>Claims <span className={styles.badge}>{number(leads.length)}</span></h2>
                {leads.length ? (
                    <div className={styles.claimList}>
                        {leads.map((lead) => (
                            <ClaimSummaryCard
                                key={String(lead.id)}
                                lead={lead}
                                state={stateByCase.get(String(lead.case_ref))}
                                signature={signatureByCase.get(String(lead.case_ref))}
                            />
                        ))}
                    </div>
                ) : (
                    <p className={styles.muted}>No claims for this client.</p>
                )}
            </section>
        </>
    )
}

export const SrdsClientDetailView = () => {
    const { clientId } = useParams()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        setLoading(true)
        api('/srds-client-details', { data: { clientId } })
            .then(({ data: response }) => setData(response))
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setLoading(false))
    }, [clientId])

    return (
        <DetailShell
            title="SRDS Client"
            breadcrumb={data?.client ? <><span aria-hidden="true">/</span><span>{[data.client.firstname, data.client.lastname].filter(Boolean).join(' ') || `Client ${clientId}`}</span></> : null}
        >
            <Status loading={loading} error={error} />
            {data?.client ? (
                <ClientBreakdown
                    client={data.client}
                    leads={data.leads || []}
                    leadStates={data.leadStates || []}
                    signatures={data.signatureLinks || []}
                />
            ) : null}
        </DetailShell>
    )
}

const WORKFLOW_STEPS: { label: string; flag: string; date: string }[] = [
    { label: 'CFA signed', flag: 'cfa_signed', date: 'cfa_signed_date' },
    { label: 'Full credit check requested', flag: 'full_credit_check_request', date: 'full_credit_check_request_date' },
    { label: 'Full credit check complete', flag: 'full_credit_check_complete', date: 'full_credit_check_complete_date' },
    { label: 'Policy number assigned', flag: 'policy_number_assigned', date: 'policy_number_assigned_date' },
    { label: 'Documents populated', flag: 'documents_populated', date: '' },
    { label: 'Claim transferred', flag: 'full_claim_transferred', date: 'full_claim_transferred_date' },
]

const Workflow = ({ state }: { state?: Row }) => {
    if (!state) return <p className={styles.muted}>No workflow record for this claim.</p>
    return (
        <ol className={styles.workflow}>
            {WORKFLOW_STEPS.map(({ label, flag, date }) => {
                const done = isSet(state[flag])
                const stamp = date ? formatDate(state[date]) : '—'
                return (
                    <li className={done ? `${styles.workflowStep} ${styles.workflowStepDone}` : styles.workflowStep} key={label}>
                        <span className={styles.workflowMarker} aria-hidden="true">
                            {done ? <FaCheck /> : null}
                        </span>
                        <span className={styles.workflowLabel}>{label}</span>
                        <span className={styles.workflowDate}>{done ? stamp : 'Outstanding'}</span>
                    </li>
                )
            })}
        </ol>
    )
}

// The signature column holds a base64 image, so it is deliberately never rendered as a field.
const SignatureCard = ({ signature }: { signature: Row }) => (
    <article className={styles.signatureCard}>
        <div className={styles.signatureHead}>
            <h3>{text(signature.type)}</h3>
            {isSet(signature.signed) ? <Pill label={text(signature.status)} tone="positive" /> : <Pill label="Unsigned" tone="warning" />}
        </div>
        <DetailList
            items={[
                { label: 'Signed', value: formatDateTime(signature.signature_date) },
                { label: 'Original signature', value: formatDateTime(signature.original_signature_date) },
                { label: 'Link opened', value: yesNo(signature.link_opened) },
                { label: 'IP address', value: text(signature.ip_address) },
                { label: 'Device', value: text(signature.device), wide: true },
            ]}
        />
    </article>
)

const ClaimBreakdown = ({
    lead,
    client,
    state,
    signatures,
}: {
    lead: Row
    client?: Row
    state?: Row
    signatures: Row[]
}) => (
    <>
        <IdentityHeader
            badge={<FaFileInvoiceDollar />}
            title={text(lead.lender)}
            reference={lead.case_ref}
            pills={<>
                {isSet(lead.base_claim) ? <Pill label="Base claim" tone="neutral" /> : null}
                {lead.account_type ? <Pill label={humanise(lead.account_type)} tone="neutral" /> : null}
                {lead.agreement_status ? <Pill label={humanise(lead.agreement_status)} tone="neutral" /> : null}
                {isSet(state?.cancelled) ? <Pill label="Cancelled" tone="warning" /> : null}
            </>}
            aside={<SummaryStrip
                label="Claim summary"
                items={[
                    { label: 'Quantum value', value: money(lead.quantum_value) },
                    { label: 'Commission', value: money(lead.commission) },
                    { label: 'Statutory interest', value: money(lead.stat_interest) },
                    { label: 'Starting balance', value: money(lead.starting_balance) },
                ]}
            />}
        />
        <div className={styles.panelGrid}>
            <Panel title="Agreement">
                <DetailList
                    items={[
                        { label: 'Policy number', value: text(lead.policy_number) },
                        { label: 'Start date', value: formatDate(lead.start_date) },
                        { label: 'Term', value: lead.length_of_contract ? `${text(lead.length_of_contract)} months` : '—' },
                        { label: 'Monthly repayment', value: money(lead.monthly_repayment) },
                        { label: 'Total payable', value: money(lead.total_amount_payable) },
                        { label: 'Total amount', value: money(lead.starting_balance) },
                        { label: 'Days accrued', value: lead.total_days ? number(lead.total_days) : '—' },
                        { label: 'Years accrued', value: text(lead.total_years) },
                    ]}
                />
            </Panel>
            <Panel title="Claim progress">
                <Workflow state={state} />
                {isSet(state?.cancelled) ? (
                    <DetailList
                        items={[
                            { label: 'Cancelled', value: formatDate(state?.cancellation_date) },
                            { label: 'Reason', value: text(state?.cancellation_reason), wide: true },
                        ]}
                    />
                ) : null}
            </Panel>
            <Panel title="Client">
                {client ? (
                    <>
                        <DetailList
                            items={[
                                { label: 'Name', value: clientName(client) },
                                { label: 'Reference', value: text(client.client_ref) },
                                { label: 'Email', value: text(client.email) },
                                { label: 'Telephone', value: text(client.telephone) },
                            ]}
                        />
                        <Link className={styles.secondaryButton} to={`/srds/client/${client.id}`}>
                            View client <FaChevronRight aria-hidden="true" />
                        </Link>
                    </>
                ) : (
                    <p className={styles.muted}>No client linked to this claim.</p>
                )}
            </Panel>
            <Panel title="Documents">
                <FileLink label="Expert report" path={lead.expert_report_link} />
                <FileLink label="Credit report" path={client?.credit_report_link} />
            </Panel>
        </div>
        <section className={styles.panel}>
            <h2>Signatures <span className={styles.badge}>{number(signatures.length)}</span></h2>
            {signatures.length ? (
                <div className={styles.signatureList}>
                    {signatures.map((signature, index) => (
                        <SignatureCard key={String(signature.id ?? index)} signature={signature} />
                    ))}
                </div>
            ) : (
                <p className={styles.muted}>No signatures recorded.</p>
            )}
        </section>
    </>
)

export const SrdsClaimDetailView = () => {
    const { claimId } = useParams()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        api('/srds-claim-details', { data: { claimId } })
            .then(({ data: response }) => setData(response))
            .catch((err) => setError(errorMessage(err)))
            .finally(() => setLoading(false))
    }, [claimId])

    return (
        <DetailShell
            title="SRDS Claim"
            breadcrumb={<>
                {data?.client ? <><span aria-hidden="true">/</span><Link to={`/srds/client/${data.client.id}`}>Client {data.client.client_ref || data.client.id}</Link></> : null}
                {data?.lead ? <><span aria-hidden="true">/</span><span>{data.lead.case_ref || `Claim ${claimId}`}</span></> : null}
            </>}
        >
            <Status loading={loading} error={error} />
            {data?.lead ? (
                <ClaimBreakdown
                    lead={data.lead}
                    client={data.client}
                    state={(data.leadStates || [])[0]}
                    signatures={[...(data.signatureLinks || []), ...(data.clientSignatures || [])]}
                />
            ) : null}
        </DetailShell>
    )
}
